import i18n from '../i18n'
import { API_ENDPOINTS } from '@/config/apiConstants'
import { BaseApiClient } from './BaseApiClient'
import { applyCatalog } from '@/i18n/messageTree'

interface I18nResponse {
    translations: Record<string, string>
    version: string
    entryCount: number
}

interface I18nVersionStatusResponse {
    version: string
    entryCount: number
    upToDate: boolean
}

class I18nSyncService extends BaseApiClient {
    private CACHE_KEY = 'i18n_cache'
    private VERSION_KEY = 'i18n_version'
    private ENTRY_COUNT_KEY = 'i18n_entry_count'

    async sync(lang: string = 'tr') {
        const cachedTranslations = this.loadCachedTranslations(lang)
        if (cachedTranslations) {
            this.applyTranslations(lang, cachedTranslations)
        }

        try {
            const cachedMetadata = this.getCachedMetadata(lang)

            if (cachedMetadata) {
                const params = new URLSearchParams({
                    lang,
                    clientVersion: cachedMetadata.version,
                    clientEntryCount: String(cachedMetadata.entryCount)
                })
                const versionStatus = await this.get<I18nVersionStatusResponse>(
                    `${API_ENDPOINTS.I18N.PUBLIC_VERSION}?${params.toString()}`
                )

                if (versionStatus.upToDate && cachedTranslations) {
                    return
                }
            }

            const response = await this.get<I18nResponse>(`${API_ENDPOINTS.I18N.PUBLIC}?lang=${lang}`)
            this.applyTranslations(lang, response.translations, cachedTranslations)

            localStorage.setItem(
                `${this.CACHE_KEY}_${lang}`,
                JSON.stringify(response.translations)
            )
            localStorage.setItem(
                `${this.VERSION_KEY}_${lang}`,
                response.version
            )
            localStorage.setItem(
                `${this.ENTRY_COUNT_KEY}_${lang}`,
                String(response.entryCount)
            )
        } catch (error) {
            if (!cachedTranslations) {
                return
            }
        }
    }

    private applyTranslations(
        lang: string,
        nextTranslations: Record<string, string>,
        previousTranslations?: Record<string, string> | null
    ) {
        const localeMessages = this.cloneMessages(i18n.global.getLocaleMessage(lang))
        applyCatalog(localeMessages, nextTranslations, previousTranslations)
        i18n.global.setLocaleMessage(lang, localeMessages)
    }

    private loadCachedTranslations(lang: string): Record<string, string> | null {
        const cached = localStorage.getItem(`${this.CACHE_KEY}_${lang}`)
        if (!cached) {
            return null
        }

        try {
            return JSON.parse(cached)
        } catch {
            return null
        }
    }

    private getCachedMetadata(lang: string): { version: string, entryCount: number } | null {
        const version = localStorage.getItem(`${this.VERSION_KEY}_${lang}`)
        const entryCountRaw = localStorage.getItem(`${this.ENTRY_COUNT_KEY}_${lang}`)

        if (!version || !entryCountRaw) {
            return null
        }

        const entryCount = Number(entryCountRaw)
        if (!Number.isFinite(entryCount) || entryCount < 0) {
            localStorage.removeItem(`${this.VERSION_KEY}_${lang}`)
            localStorage.removeItem(`${this.ENTRY_COUNT_KEY}_${lang}`)
            return null
        }

        return {
            version,
            entryCount
        }
    }

    private cloneMessages(messages: unknown) {
        return JSON.parse(JSON.stringify(messages || {}))
    }

    async getAll(params: { page: number, size: number, query?: string, locale?: string, module?: string, modules?: string[] }) {
        // If modules array is provided, backend expects 'modules': 'A,B' or multiple params
        // Axios handles arrays by default as modules[]=A&modules[]=B which Spring Boot understands if configured,
        // or we can join them. Let's rely on standard params first.
        return this.get<any>('/i18n/all', { params })
    }

    async getModules() {
        return this.get<string[]>('/i18n/modules')
    }

    async save(request: any): Promise<any> {
        return this.put<any>('/i18n', request)
    }

    async remove(id: string): Promise<any> {
        return this.delete<any>(`/i18n/${id}`)
    }

    /** Toplu sözlük içe aktarma — JWT'li istemciden gider; uç artık auth + i18n:update ister. */
    async bulkImport(payload: unknown): Promise<any> {
        return this.post<any>('/i18n/bulk/import', payload)
    }
}

export const i18nSyncService = new I18nSyncService()
